import { useStorageItem } from "@capacitor-community/storage-react";
import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonPage,
  IonRow,
  IonTitle,
  IonToolbar,
  useIonLoading,
  useIonModal,
  useIonToast,
  useIonViewDidEnter,
} from "@ionic/react";
import QRCodeModal from "components/QRCodeModal";
import { QRCodeScannedModal } from "components/QRCodeScannedModal";
import { QRWebModal } from "components/QRWebModal";
import { AuthContext } from "contexts/AuthContext";
import useAccount from "hooks/useAccount";
import useMerchantProfile from "hooks/useMerchantProfile";
import { qrCodeOutline, timeOutline } from "ionicons/icons";
import { useContext, useEffect, useRef, useState } from "react";
import { AccountStore } from "stores/AccountStore";
import useSound from "use-sound";
import client from "utils/Api";
import Keypad from "../components/Keypad";
import useCodes from "../hooks/useCodes";
import openSound from "../sounds/open.wav";
import "./Checkout.css";
import styles from "./Home.module.css";

const Checkout = () => {
  const { authState } = useContext(AuthContext);
  const [loadAccount, { data, loading }] = useAccount(authState);
  const { data: lspData, loading: lspLoading } = useMerchantProfile(
    authState.user.uid,
    true
  );
  const account = AccountStore.useState((s) => s.account);
  const [rawValue, setRawValue] = useState();
  const [invoice, setInvoice] = useState();
  const [formattedValue, setFormattedValue] = useState();
  const [scannerKey, setScannerKey] = useStorageItem(
    "scannerKey",
    account?.merchant?.oshi_scanner_keys.length > 0
      ? account?.merchant?.oshi_scanner_keys[0].id
      : null
  );
  const [presentLoading, dismissLoading] = useIonLoading();
  const [showToast] = useIonToast();
  const [, setCodes] = useCodes();

  const pageRef = useRef();

  const [play] = useSound(openSound);

  const [QRData, setQRData] = useState(false);
  const axiosClient = client();

  const handleScan = (data) => {
    if (data) {
      setQRData(data);
      play();
      handleSuccess(data);
    }
  };

  const handleError = (err) => {
    console.error(err);
  };

  const start = async () => {
    presentWebModal({
      presentingElement: pageRef.current,
    });
  };

  const handleSuccess = (data) => {
    setQRData(data);
    dismissWebModal();
    if (!data.startsWith("ln") && !data.startsWith("LN")) {
      play();
      present({
        presentingElement: pageRef.current,
      });
    }
  };

  const [present, dismiss] = useIonModal(QRCodeScannedModal, {
    dismiss: () => dismiss(),
    code: QRData,
    set: () => setQRData(),
    scan: () => start(),
    scanner_key: scannerKey,
  });

  const [presentWebModal, dismissWebModal] = useIonModal(QRWebModal, {
    dismiss: () => dismissWebModal(),
    set: () => setQRData(),
    scan: handleScan,
    error: handleError,
  });

  const [presentQRModal, dismissQRModal] = useIonModal(QRCodeModal, {
    data: invoice && invoice.lightning_invoice.payreq,
    invoiceId: invoice?.id,
    onClose: () => dismissQRModal(),
    subtitle: invoice?.description,
    title: `$${invoice?.fiat_value}`,
  });

  const handleAdd = async (code) => {
    setCodes((codes) => {
      return [...codes, code];
    });
    showToast({
      header: "Success!",
      message: "Invoice stored successfully.",
      duration: 3000,
      color: "primary",
    });
  };

  const handleClick = (number) => {
    const newValue = rawValue
      ? rawValue.toString() + number.toString()
      : number.toString();
    setRawValue(newValue);
  };

  const handleRemove = () => {
    if (typeof rawValue !== "undefined" && rawValue.length > 0)
      setRawValue(rawValue.slice(0, -1));
  };

  const onSubmit = async () => {
    try {
      presentLoading({
        duration: 3000,
        spinner: "circles",
      });
      if (scannerKey) {
        const payload = {
          amount: rawValue,
          description: `Please pay ${formattedValue}`,
          scanner_key: scannerKey,
        };
        const res = await axiosClient.post("/scanner/invoices", payload);
        dismiss();
        handleAdd(res.data.charge);
        setInvoice(res.data.charge);
        presentQRModal({
          presentingElement: pageRef.current,
          swipeToClose: true,
        });
      } else {
        showToast({
          header: "Failed!",
          message: "You need a permissions key to perform this action.",
          duration: 3000,
          color: "danger",
        });
      }
    } catch (e) {
      console.log("error", e);
      dismissLoading();
    }
  };

  useEffect(() => {
    if (account?.merchant?.oshi_scanner_keys) {
      setScannerKey(account.merchant?.oshi_scanner_keys[0].id);
    }
  }, [account, setScannerKey]);

  useEffect(() => {
    if (rawValue) {
      var usd = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(parseFloat(rawValue));

      setFormattedValue(usd);
    } else {
      setFormattedValue(undefined);
    }
  }, [rawValue]);

  useEffect(() => {
    if (data?.users_by_pk) {
      AccountStore.update((s) => {
        s.account = { ...data.users_by_pk };
      });
      if (data.users_by_pk?.merchant?.oshi_scanner_keys)
        setScannerKey(data.users_by_pk?.merchant?.oshi_scanner_keys[0]?.id);
    }
  }, [data, setScannerKey]);

  useIonViewDidEnter(() => {
    loadAccount();
  });

  return (
    <IonPage ref={pageRef} className={"checkout-page-root"}>
      <IonHeader mode="ios" class="ion-no-border">
        <IonToolbar className={"background-color"}>
          <IonButtons slot="start">
            <IonButton
              slot="start"
              routerLink="/checkout/history"
              routerDirection="back"
            >
              <IonIcon icon={timeOutline} color="primary" />
            </IonButton>
          </IonButtons>
          <IonTitle>
            <strong>OSHI</strong>
          </IonTitle>
          <IonButtons slot="end">
            <IonButton slot="end" onClick={() => start()}>
              <IonIcon icon={qrCodeOutline} color="primary" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        {!loading && !lspLoading && account?.id && !account?.merchant?.id && (
          <IonItem
            lines="none"
            color="danger"
            className={"alert-banner"}
            routerLink={"/account/business"}
          >
            <IonLabel>Continue account setup to enable</IonLabel>
          </IonItem>
        )}
        {!loading &&
          !lspLoading &&
          account?.merchant?.id &&
          !lspData?.merchants[0]?.provider_keys &&
          !lspData?.merchants[0]?.opennode_account_id &&
          !lspData?.merchants[0]?.opennode_key_public && (
            <IonItem
              lines="none"
              color="danger"
              className={"alert-banner"}
              routerLink={"/account/lsp"}
            >
              <IonLabel>Continue LSP setup to enable</IonLabel>
            </IonItem>
          )}
        <IonGrid
          className={`${styles.mainGrid} ion-text-center ion-padding-top`}
        >
          <IonRow>
            <IonCol size="12">
              <h1 className={"checkout-header"}>{formattedValue}</h1>
              {!formattedValue && (
                <p className={"checkout-p"}>Enter an amount</p>
              )}
            </IonCol>
          </IonRow>
          <Keypad handleRemove={handleRemove} handleClick={handleClick} />
          <IonRow className={`${styles.bottomrow}`}>
            <IonButtons>
              <IonButton
                slot="start"
                fill="outline"
                color="medium"
                onClick={() => setRawValue(undefined)}
              >
                {" "}
                Cancel{" "}
              </IonButton>
              <IonButton
                disabled={
                  !(parseFloat(rawValue) > 0) ||
                  (account && !account?.merchant) ||
                  (!lspData?.merchants[0]?.provider_keys &&
                    !lspData?.merchants[0]?.opennode_account_id &&
                    !lspData?.merchants[0]?.opennode_key_public)
                }
                slot="end"
                fill="solid"
                color="primary"
                onClick={() => onSubmit()}
              >
                {" "}
                Accept{" "}
              </IonButton>
            </IonButtons>
          </IonRow>
        </IonGrid>
      </IonContent>
    </IonPage>
  );
};

export default Checkout;
